import type { ReactNode } from 'react';

import { SettingsPopoverPanel } from '@/components/page-editor/settings-panel/panel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const POPOVER_MAX_HEIGHT = 'max-h-[min(82vh,760px)]';

interface SettingsPopoverShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  trigger: ReactNode;
  layoutContent: ReactNode;
  contentContent: ReactNode;
  backgroundContent: ReactNode;
  settingsContent: ReactNode;
  buttonContent?: ReactNode;
  showLayoutTab?: boolean;
  trackMovingAnchor?: boolean;
}

export function SettingsPopoverShell({
  open,
  onOpenChange,
  title,
  trigger,
  layoutContent,
  contentContent,
  backgroundContent,
  settingsContent,
  buttonContent,
  showLayoutTab = true,
  trackMovingAnchor = false,
}: SettingsPopoverShellProps) {
  const defaultTab = showLayoutTab ? 'layout' : 'content';

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        hideWhenDetached
        updatePositionStrategy={trackMovingAnchor ? 'always' : 'optimized'}
        className={cn(
          'z-[130] w-[32rem] overflow-hidden rounded-md border bg-background p-0 shadow-2xl',
          POPOVER_MAX_HEIGHT,
        )}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <SettingsPopoverPanel
          title={title}
          onRequestClose={() => onOpenChange(false)}
          className={POPOVER_MAX_HEIGHT}
        >
          <Tabs defaultValue={defaultTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList
              variant="underline"
              fullWidth={false}
              className="mb-0 h-auto max-w-none gap-1 rounded-none p-0"
            >
              {showLayoutTab ? <TabsTrigger value="layout">Layout</TabsTrigger> : null}
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="background">Background</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              {buttonContent ? <TabsTrigger value="button">Button</TabsTrigger> : null}
            </TabsList>

            <div className="max-h-[min(60vh,560px)] overflow-y-auto px-1 pt-2 pb-4">
              {showLayoutTab ? (
                <TabsContent value="layout" className="mt-0">
                  {layoutContent}
                </TabsContent>
              ) : null}
              <TabsContent value="content" className="mt-0">
                {contentContent}
              </TabsContent>
              <TabsContent value="background" className="mt-0">
                {backgroundContent}
              </TabsContent>
              <TabsContent value="settings" className="mt-0">
                {settingsContent}
              </TabsContent>
              {buttonContent ? (
                <TabsContent value="button" className="mt-0">
                  {buttonContent}
                </TabsContent>
              ) : null}
            </div>
          </Tabs>
        </SettingsPopoverPanel>
      </PopoverContent>
    </Popover>
  );
}
